/**
 * OnboardingPreview — visual mockup of the proposed new-user onboarding wizard.
 *
 * NOT a live page — this is a clickable static preview so the owner can walk
 * through the layout and approve the design before we build the real thing.
 *
 * Route: /onboarding-preview  (no auth)
 *
 * Proposed flow (option A: guided full-screen wizard on first login):
 *   0. Welcome
 *   1. Who are you?           (Developer/Builder | General Contractor — captured, doesn't branch)
 *   2. Confirm your details   (personal + business prefilled from signup; EIN optional for business; skippable)
 *   3. Add your contractors   (manual add; skippable — "I'll do this later")
 *   4. Collect W-9 / COI      (per contractor: send invite link OR upload on their behalf; skippable)
 *   5. You're all set         (recap + start first project / go to dashboard)
 *
 * Every step is skippable. This preview is web-only; the real build ships to web + mobile.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Rocket, CheckCircle2, Building2, HardHat, User, Briefcase, Users, ShieldCheck,
  Mail, Upload, ArrowRight, ArrowLeft, X, Check, FileText, Plus,
  Image as ImageIcon, Link2, Landmark,
} from 'lucide-react';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

// Recurring DrawReady brand mark — the app's blue rounded-square "D".
// A constant brand reminder on every step (matches the logo used app-wide).
function BrandMark({ size = 40 }) {
  return (
    <div
      className="flex items-center justify-center font-extrabold text-white shadow-sm"
      style={{ width: size, height: size, background: '#2563EB', borderRadius: size * 0.26, fontSize: size * 0.52, lineHeight: 1 }}
      data-testid="ob-brandmark"
    >
      D
    </div>
  );
}

const STEPS = [
  { key: 'welcome', label: 'Welcome' },
  { key: 'persona', label: 'Who you are' },
  { key: 'details', label: 'Your details' },
  { key: 'contractors', label: 'Add contractors' },
  { key: 'compliance', label: 'W-9 & COI' },
  { key: 'done', label: 'All set' },
];

// -------------------------------------------------------------------
// Shell: left step rail + right content area (full screen)
// -------------------------------------------------------------------
export default function OnboardingPreview() {
  const [step, setStep] = useState(0);
  const [persona, setPersona] = useState('developer');
  const isBusiness = true; // pretend this account signed up as a business

  const pct = Math.round((step / (STEPS.length - 1)) * 100);
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="min-h-screen flex" style={{ background: '#f1f5f9' }} data-testid="onboarding-preview">
      {/* Left rail */}
      <aside className="hidden lg:flex flex-col w-72 flex-shrink-0 p-8 text-white" style={{ background: NAVY }}>
        <div className="mb-10">
          <img src="/logo-demo-white.png" alt="DrawReady" className="h-8 w-auto" />
          <p className="text-[11px] text-blue-200 mt-2">Construction Finance</p>
        </div>

        <p className="text-[11px] font-bold uppercase tracking-widest text-blue-300 mb-4">Getting started</p>
        <ol className="space-y-1.5 flex-1">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <li key={s.key} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition ${active ? 'bg-white/12' : ''}`}>
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                  style={{ background: done ? GREEN : active ? '#fff' : 'rgba(255,255,255,0.12)', color: done ? '#fff' : active ? NAVY : '#cbd5e1' }}
                >
                  {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                <span className={`text-sm ${active ? 'font-bold text-white' : done ? 'text-blue-200' : 'text-blue-300/70'}`}>{s.label}</span>
              </li>
            );
          })}
        </ol>

        <div className="mt-6">
          <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: GREEN }} />
          </div>
          <p className="text-[11px] text-blue-200 mt-2">{pct}% complete · takes ~3 min</p>
        </div>
      </aside>

      {/* Right content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-slate-200 bg-white">
          <div className="lg:hidden">
            <img src="/logo-full.png" alt="DrawReady" className="h-7 w-auto" />
          </div>
          <span className="hidden lg:block text-sm text-slate-400">Step {step + 1} of {STEPS.length}</span>
          {step > 0 && step < STEPS.length - 1 && (
            <button className="text-sm font-semibold text-slate-400 hover:text-slate-700 inline-flex items-center gap-1" onClick={next} data-testid="ob-skip">
              Skip for now <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-6 sm:px-10 py-10">
            {step === 0 && <WelcomeStep />}
            {step === 1 && <PersonaStep persona={persona} setPersona={setPersona} />}
            {step === 2 && <DetailsStep isBusiness={isBusiness} />}
            {step === 3 && <ContractorsStep />}
            {step === 4 && <ComplianceStep />}
            {step === 5 && <DoneStep />}
          </div>
        </div>

        {/* Footer nav */}
        <div className="border-t border-slate-200 bg-white px-6 sm:px-10 py-4 flex items-center justify-between">
          <button
            onClick={back}
            disabled={step === 0}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold ${step === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="ob-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="text-white" style={{ background: NAVY }} data-testid="ob-next">
              {step === 0 ? 'Get started' : 'Continue'} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button className="text-white" style={{ background: GREEN }} data-testid="ob-finish">
              <Rocket className="w-4 h-4 mr-1.5" /> Go to my dashboard
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

// -------------------------------------------------------------------
// Step 0 — Welcome
// -------------------------------------------------------------------
function WelcomeStep() {
  return (
    <div className="text-center pt-6" data-testid="ob-step-welcome">
      <div className="mx-auto mb-5 flex justify-center">
        <BrandMark size={64} />
      </div>
      <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GREEN }}>Welcome to DrawReady</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2">Let’s get your account set up, Alex</h1>
      <p className="text-slate-500 mt-3 max-w-lg mx-auto text-[15px] leading-relaxed">
        A few quick steps so DrawReady works the way you do — confirm your details, bring in your contractors,
        and collect the compliance docs banks ask for. You can skip anything and finish it later.
      </p>
      <div className="grid sm:grid-cols-3 gap-3 mt-8 text-left">
        {[
          { icon: User, t: 'Confirm your info', s: 'Business & contact details' },
          { icon: Users, t: 'Add contractors', s: 'Your trusted trades' },
          { icon: ShieldCheck, t: 'Collect W-9 / COI', s: 'Bank-ready compliance' },
        ].map((c) => (
          <div key={c.t} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center mb-2"><c.icon className="w-5 h-5" style={{ color: NAVY }} /></div>
            <p className="text-sm font-bold text-slate-900">{c.t}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.s}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Step 1 — Persona (Developer/Builder | GC)
// -------------------------------------------------------------------
function PersonaStep({ persona, setPersona }) {
  const OPTIONS = [
    { key: 'developer', icon: Building2, t: 'Developer / Builder', s: 'I build and manage my own projects and financing.' },
    { key: 'gc', icon: HardHat, t: 'General Contractor', s: 'I manage builds & draws on behalf of owners/clients.' },
    { key: 'lender', icon: Landmark, t: 'Lender', s: 'I finance construction/commercial loans and monitor my borrowers.' },
  ];
  return (
    <div data-testid="ob-step-persona">
      <StepHeader eyebrow="A quick question" title="How do you use DrawReady?" sub="This tailors your home view. You can change it anytime in Settings." />
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {OPTIONS.map((o) => {
          const active = persona === o.key;
          return (
            <button
              key={o.key}
              onClick={() => setPersona(o.key)}
              className={`text-left rounded-2xl border-2 p-5 transition ${active ? 'bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
              style={active ? { borderColor: NAVY } : {}}
              data-testid={`ob-persona-${o.key}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: active ? NAVY : '#eff6ff' }}>
                  <o.icon className="w-6 h-6" style={{ color: active ? '#fff' : NAVY }} />
                </div>
                {active && <CheckCircle2 className="w-5 h-5" style={{ color: NAVY }} />}
              </div>
              <p className="text-base font-bold text-slate-900 mt-3">{o.t}</p>
              <p className="text-sm text-slate-500 mt-1">{o.s}</p>
            </button>
          );
        })}
      </div>
      {persona === 'lender' && (
        <a
          href="/lender-view-preview"
          className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white rounded-xl px-4 py-2.5"
          style={{ background: NAVY }}
          data-testid="ob-lender-preview-link"
        >
          <Landmark className="w-4 h-4" /> Preview the Lender portfolio view <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Step 2 — Confirm details (prefilled from signup) + optional EIN/logo
// -------------------------------------------------------------------
function DetailsStep({ isBusiness }) {
  return (
    <div data-testid="ob-step-details">
      <StepHeader eyebrow="Confirm your details" title="Does this look right?" sub="We pulled this from your sign-up. Tweak anything, or skip — you can edit it later in Settings." />

      <div className="mt-6 space-y-5">
        <Field label="Full name" value="Alex Morgan" />
        <Field label="Email" value="alex@morganbuilds.com" />
        <Field label="Phone" value="(319) 555-0142" />

        {isBusiness && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4"><Briefcase className="w-4 h-4" style={{ color: GREEN }} /> Business details</p>
            <div className="space-y-4">
              <Field label="Company name" value="Morgan Builds LLC" />
              <Field label="EIN (optional)" value="" placeholder="12-3456789" optional />
              <div>
                <label className="text-xs font-semibold text-slate-500">Company logo (optional)</label>
                <div className="mt-1.5 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                  <button className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50">
                    <Upload className="w-4 h-4" /> Upload logo
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">Appears on contractor emails, bid sheets & bank packets.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Step 3 — Add contractors
// -------------------------------------------------------------------
function ContractorsStep() {
  const rows = [
    { name: 'Timberline Framing', trade: 'Framing', email: 'ops@timberline.co' },
    { name: 'Brightwire Electric', trade: 'Electrical', email: 'hello@brightwire.co' },
  ];
  return (
    <div data-testid="ob-step-contractors">
      <StepHeader eyebrow="Your team" title="Add your contractors" sub="Bring in the trades you work with. You can also import a spreadsheet — or skip and add them later." />

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><HardHat className="w-4 h-4" style={{ color: GREEN }} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{r.name}</p>
              <p className="text-[11px] text-slate-400 truncate">{r.trade} · {r.email}</p>
            </div>
            <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} />
          </div>
        ))}

        {/* Add-row inline form (mock) */}
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 p-4 bg-slate-50">
          <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Company / name" />
          <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Email" />
          <button className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-white rounded-lg px-4 py-2" style={{ background: NAVY }}>
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <button className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
        <FileText className="w-4 h-4" /> Import contractors from a spreadsheet
      </button>
    </div>
  );
}

// -------------------------------------------------------------------
// Step 4 — Collect W-9 / COI (both: invite link + upload on behalf)
// -------------------------------------------------------------------
function ComplianceStep() {
  const rows = [
    { name: 'Timberline Framing', w9: 'received', coi: 'invited' },
    { name: 'Brightwire Electric', w9: 'missing', coi: 'missing' },
  ];
  return (
    <div data-testid="ob-step-compliance">
      <StepHeader
        eyebrow="Bank-ready compliance"
        title="Collect W-9 & COI"
        sub="Two ways per contractor: send them a secure link to upload themselves, or drop the files in on their behalf. Banks ask for these before funding."
      />

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-4 py-2.5 bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-400">
          <span>Contractor</span><span className="w-24 text-center">W-9</span><span className="w-24 text-center">COI</span>
        </div>
        {rows.map((r) => (
          <div key={r.name} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center px-4 py-3 border-b border-slate-100 last:border-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{r.name}</p>
            <DocPill status={r.w9} />
            <DocPill status={r.coi} />
          </div>
        ))}
      </div>

      {/* Action affordances */}
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center mb-2"><Link2 className="w-5 h-5" style={{ color: NAVY }} /></div>
          <p className="text-sm font-bold text-slate-900">Send an upload link</p>
          <p className="text-xs text-slate-500 mt-0.5">Contractor uploads their own W-9 & COI via a secure portal.</p>
          <button className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white rounded-lg px-3 py-2" style={{ background: NAVY }}>
            <Mail className="w-4 h-4" /> Send invite
          </button>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center mb-2"><Upload className="w-5 h-5" style={{ color: GREEN }} /></div>
          <p className="text-sm font-bold text-slate-900">Upload on their behalf</p>
          <p className="text-xs text-slate-500 mt-0.5">Already have the files? Drag & drop them here for the contractor.</p>
          <button className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white rounded-lg px-3 py-2" style={{ background: GREEN }}>
            <Upload className="w-4 h-4" /> Upload files
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Step 5 — Done
// -------------------------------------------------------------------
function DoneStep() {
  const recap = [
    { t: 'Account details confirmed', ok: true },
    { t: '2 contractors added', ok: true },
    { t: '1 of 2 W-9s collected', ok: false },
    { t: 'COIs — 1 invite sent', ok: false },
  ];
  return (
    <div className="text-center pt-6" data-testid="ob-step-done">
      <div className="mx-auto mb-5 flex justify-center">
        <BrandMark size={64} />
      </div>
      <h1 className="text-3xl font-extrabold text-slate-900">You’re all set, Alex</h1>
      <p className="text-slate-500 mt-2 max-w-md mx-auto">Here’s where you landed. Anything you skipped stays on your dashboard checklist so you can finish it whenever.</p>

      <div className="mt-7 rounded-2xl border border-slate-200 bg-white p-5 text-left max-w-md mx-auto">
        {recap.map((r) => (
          <div key={r.t} className="flex items-center gap-3 py-2">
            {r.ok
              ? <CheckCircle2 className="w-5 h-5" style={{ color: GREEN }} />
              : <span className="w-5 h-5 rounded-full border-2 border-amber-300 flex items-center justify-center text-[10px] font-bold text-amber-500">!</span>}
            <span className={`text-sm ${r.ok ? 'text-slate-700' : 'text-slate-500'}`}>{r.t}</span>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl p-5 text-white max-w-md mx-auto text-left" style={{ background: NAVY }}>
        <p className="text-sm font-bold flex items-center gap-2"><Rocket className="w-4 h-4" style={{ color: '#a8d977' }} /> Ready to build?</p>
        <p className="text-blue-100 text-sm mt-1">Start your first project — or migrate one that’s already underway.</p>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Small shared bits
// -------------------------------------------------------------------
function StepHeader({ eyebrow, title, sub }) {
  return (
    <div>
      <div className="mb-4"><BrandMark size={36} /></div>
      <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GREEN }}>{eyebrow}</p>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">{title}</h1>
      {sub && <p className="text-slate-500 mt-2 text-[15px] leading-relaxed">{sub}</p>}
    </div>
  );
}

function Field({ label, value, placeholder, optional }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500">{label}</label>
      <input
        defaultValue={value}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200"
      />
      {optional && !value && <p className="text-[11px] text-slate-400 mt-1">Optional — helps auto-fill 1099s later.</p>}
    </div>
  );
}

function DocPill({ status }) {
  const map = {
    received: { label: 'Received', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Check },
    invited: { label: 'Invited', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Mail },
    missing: { label: 'Add', cls: 'bg-slate-50 text-slate-500 border-slate-200', icon: Plus },
  };
  const m = map[status] || map.missing;
  const Icon = m.icon;
  return (
    <span className={`w-24 inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}
